import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import axios from "./axios";

// --- Types -------------------------------------------------------------------

export type PlanTier = "saas" | "ai_addon";

export type Plan = {
  id: number;
  slug: string;
  name: string;
  tier: PlanTier;
  price_cents_monthly: number;
  price_cents_annual: number;
  currency: string;
  max_vehicles: number | null;
  features: string[];
  is_active: boolean;
  sort_order: number;
  price_monthly?: number;
  price_annual?: number;
};

export type BillingCycle = "monthly" | "annual";

export type SubscriptionStatus =
  | "trialing"
  | "active"
  | "past_due"
  | "paused"
  | "canceled"
  | "unpaid"
  | "incomplete"
  | "incomplete_expired";

export type Invoice = {
  id: number;
  number: string | null;
  amount: number;
  total: number;
  currency: string;
  status: "draft" | "open" | "paid" | "void" | "uncollectible";
  paid_at: string | null;
  pdf_url: string | null;
  hosted_url: string | null;
  period_start: string | null;
  period_end: string | null;
};

export type Subscription = {
  id: number;
  plan: Plan;
  /** AI Layer add-on attached to the subscription (checkout or admin force). */
  ai_addon: Plan | null;
  /**
   * Which provider bills this subscription.
   *
   * Fixed at creation and never changed: switching the platform's active
   * provider only affects NEW subscriptions, so an existing subscriber may be
   * on a different provider from the one a new customer would get today.
   */
  provider: "stripe" | "revolut";
  /**
   * Effective feature slugs computed by the API: SaaS plan features with
   * "everything_in_*" inheritance expanded + AI add-on features merged.
   * Prefer this over plan.features for gating.
   */
  features: string[];
  billing_cycle: BillingCycle;
  vehicle_quantity: number;
  status: SubscriptionStatus;
  is_active: boolean;
  on_trial: boolean;
  on_grace_period: boolean;
  trial_ends_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  canceled_at: string | null;
  ends_at: string | null;
  metadata: Record<string, any> | null;
  invoices: Invoice[];
};

// --- Query keys ----------------------------------------------------------------

export const billingKeys = {
  plans: () => ["billing-plans"] as const,
  config: () => ["billing-config"] as const,
  subscription: (companyId: number) =>
    ["billing-subscription", "idFleet", companyId] as const,
};

/**
 * Public billing configuration.
 *
 * The trial length is admin-managed server-side, so it cannot be a constant
 * here. Register.tsx previously hardcoded `days: 14` and, worse, rendered a
 * concrete "you will be charged on {date}" from it — if an admin changed the
 * trial length or switched trials off, the signup page stated a commitment the
 * billing system would not honour.
 */
export type BillingConfig = {
  trial_enabled: boolean;
  /** 0 when trials are disabled. */
  trial_days: number;
  /**
   * Already the AND of the admin switch and the active provider's ability to
   * honour a code, computed server-side. Do not re-derive it from
   * `capabilities.promo_codes` here — one source, one answer.
   */
  promo_enabled: boolean;
  /** Which provider will handle the next checkout. */
  provider: "stripe" | "revolut";
  /**
   * What the active provider supports. Stripe and Revolut are not
   * feature-equivalent, so controls are gated on these rather than assumed:
   * a portal button rendered while Revolut is active is a button that 501s.
   */
  capabilities: {
    promo_codes: boolean;
    /**
     * Where the customer types a promo code.
     *
     * 'provider' — the hosted page has its own field (Stripe). Do NOT render
     *              one here as well: two entry points can disagree.
     * 'app'      — the hosted page has none (Revolut), so the code must be
     *              collected before the redirect and sent with the checkout
     *              request, or the discount is unreachable.
     */
    promo_collected_by: "provider" | "app";
    billing_portal: boolean;
    hosted_invoices: boolean;
    quantity_updates: boolean;
  };
};

/**
 * Used only before the first response lands.
 *
 * Defaults to Stripe's full capability set on purpose: this is the historical
 * behaviour, so a slow config request shows the UI users already know rather
 * than briefly hiding controls that do work.
 */
export const DEFAULT_BILLING_CONFIG: BillingConfig = {
  trial_enabled: true,
  trial_days: 14,
  promo_enabled: true,
  provider: "stripe",
  capabilities: {
    promo_codes: true,
    // Stripe's historical behaviour: its hosted page collects the code.
    promo_collected_by: "provider",
    billing_portal: true,
    hosted_invoices: true,
    quantity_updates: true,
  },
};

export const useGetBillingConfig = () => {
  const { data, ...res } = useQuery({
    queryKey: billingKeys.config(),
    queryFn: async (): Promise<BillingConfig> => {
      const res = await axios.get(`/billing/config`);
      const payload = res?.data?.data;
      if (!payload) return DEFAULT_BILLING_CONFIG;

      // Merged rather than spread wholesale: an older API build that predates
      // the provider work returns no `capabilities` key at all, and a bare
      // spread would leave it undefined and crash every gated control.
      return {
        ...DEFAULT_BILLING_CONFIG,
        ...payload,
        capabilities: {
          ...DEFAULT_BILLING_CONFIG.capabilities,
          ...(payload.capabilities ?? {}),
        },
      };
    },
    // Operational config, not per-user data: safe to hold for a while.
    staleTime: 5 * 60 * 1000,
  });

  return { config: data ?? DEFAULT_BILLING_CONFIG, ...res };
};

// --- Queries ---------------------------------------------------------------------

export const useGetPlans = (tier?: PlanTier) => {
  const { data, error, ...res } = useQuery({
    queryKey: [...billingKeys.plans(), tier ?? "all"],
    queryFn: async (): Promise<Plan[]> => {
      const data = await axios.get(`/billing/plans`, {
        params: tier ? { tier } : undefined,
      });
      return data?.data?.data ?? [];
    },
  });

  return { plans: data ?? [], error, ...res };
};

export const useGetSubscription = (companyId: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: billingKeys.subscription(companyId),
    enabled: !!companyId,
    queryFn: async (): Promise<Subscription | null> => {
      const data = await axios.get(`/companies/${companyId}/billing/subscription`);
      return data?.data?.data ?? null;
    },
  });

  return { subscription: data ?? null, error, ...res };
};

// --- Mutations -------------------------------------------------------------------

interface CheckoutSessionInput {
  plan_slug: string;
  billing_cycle: BillingCycle;
  vehicle_quantity: number;
  ai_addon_slug?: string | null;
  email?: string;
  /**
   * Only sent when capabilities.promo_collected_by === "app".
   * The API re-validates it, so this is a request, not an instruction.
   */
  promo_code?: string | null;
}

/**
 * Where the checkout reference is parked while the customer is off-site.
 *
 * Stripe substitutes {CHECKOUT_SESSION_ID} into success_url, so the reference
 * comes back in the query string for free. Revolut has no such placeholder --
 * its setup_order_redirect_url is returned verbatim -- so the subscription id
 * would be lost on the way back and the return page would have nothing to
 * reconcile with, leaving it dependent on the webhook we deliberately took off
 * the happy path.
 *
 * sessionStorage rather than localStorage: this is scoped to one checkout
 * attempt in one tab, and a stale reference surviving a browser restart could
 * have the return page reconciling against an abandoned subscription.
 */
export const CHECKOUT_REF_KEY = "codenekt.checkout.reference";

export const rememberCheckoutReference = (reference: string): void => {
  try {
    sessionStorage.setItem(CHECKOUT_REF_KEY, reference);
  } catch {
    // Private browsing or a storage quota error. Not fatal: the return page
    // still polls, and the webhook still lands.
  }
};

export const readCheckoutReference = (): string | null => {
  try {
    return sessionStorage.getItem(CHECKOUT_REF_KEY);
  } catch {
    return null;
  }
};

export const clearCheckoutReference = (): void => {
  try {
    sessionStorage.removeItem(CHECKOUT_REF_KEY);
  } catch {
    /* nothing to clean up */
  }
};

/**
 * Starts (or changes) a subscription. On success the caller should redirect to
 * the returned `url` -- the active provider hosts the payment page, then
 * returns the customer to our success URL.
 */
export const useCreateCheckoutSession = (companyId: number) => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (input: CheckoutSessionInput) => {
      const data = await axios.post(
        `/companies/${companyId}/billing/checkout-session`,
        input
      );
      const session = data.data as { id: string; url: string };

      // Stashed before the redirect for the reason above. Harmless for Stripe,
      // where the query string wins anyway.
      if (session?.id) rememberCheckoutReference(session.id);

      return session;
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? t("billing.errorLoad"));
    },
  });
};

/**
 * A promo code the customer has entered and the API has accepted.
 *
 * `discount_label` is rendered verbatim ('20%' or '15.00 EUR') rather than
 * being reconstructed client-side, so the wording cannot drift from what the
 * admin configured.
 */
export interface ValidatedPromoCode {
  id: number;
  code: string;
  name: string | null;
  discount_label: string;
  discount_type: "percent" | "amount";
  percent_off: number | null;
  amount_off_cents: number | null;
  duration: "once" | "repeating" | "forever";
  duration_in_months: number | null;
}

export interface PromoValidationResult {
  valid: boolean;
  reason?: string;
  message?: string;
  promo_code?: ValidatedPromoCode;
}

/**
 * Check a promo code before checkout.
 *
 * Advisory only. The API re-validates the code inside the checkout call, so a
 * client that skips this (or races it) still cannot apply an expired or
 * ineligible code — the discount is decided server-side.
 *
 * Worth calling anyway: the API returns a specific reason ('this code expired
 * on 12 May') rather than a bare rejection, and showing that at entry time is
 * the difference between a customer fixing a typo and a customer filing a
 * support ticket.
 */
export const useValidatePromoCode = (companyId: number) =>
  useMutation({
    mutationFn: async (input: {
      code: string;
      plan_id?: number | null;
    }): Promise<PromoValidationResult> => {
      const res = await axios.post(
        `/companies/${companyId}/billing/promo-code/validate`,
        { code: input.code, plan_id: input.plan_id ?? null }
      );
      return (res?.data?.data ?? res?.data) as PromoValidationResult;
    },
    // No toast here: the caller renders the reason inline next to the field,
    // which is where someone correcting a code is already looking.
  });

/**
 * Opens the provider's self-service portal for plan / payment-method /
 * invoice management.
 *
 * Only Stripe offers one. Check `config.capabilities.billing_portal` before
 * rendering the control that calls this -- the API answers 501 for a provider
 * that has no portal, which is a correct response but a poor experience if the
 * button was visible in the first place.
 */
export const useCreatePortalSession = (companyId: number) => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async () => {
      const data = await axios.post(
        `/companies/${companyId}/billing/portal-session`
      );
      return data.data as { url: string };
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? t("billing.errorLoad"));
    },
  });
};

export const useCancelSubscription = (companyId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      subscriptionId,
      immediately = false,
    }: {
      subscriptionId: number;
      immediately?: boolean;
    }) => {
      const data = await axios.post(
        `/companies/${companyId}/billing/subscription/${subscriptionId}/cancel`,
        { immediately }
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: billingKeys.subscription(companyId) });
      toast.success(t("common.changeSaved", "Change saved"));
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message);
    },
  });
};

export const useResumeSubscription = (companyId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (subscriptionId: number) => {
      const data = await axios.post(
        `/companies/${companyId}/billing/subscription/${subscriptionId}/resume`
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: billingKeys.subscription(companyId) });
      toast.success(t("common.changeSaved", "Change saved"));
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message);
    },
  });
};

export const useUpdateSubscriptionQuantity = (companyId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      subscriptionId,
      quantity,
    }: {
      subscriptionId: number;
      quantity: number;
    }) => {
      const data = await axios.put(
        `/companies/${companyId}/billing/subscription/${subscriptionId}/quantity`,
        { quantity }
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: billingKeys.subscription(companyId) });
      toast.success(t("common.changeSaved", "Change saved"));
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message);
    },
  });
};
